import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import BuyOnStoreButton from "web/react/components/buy-on-store-button/buy-on-store-button";
import ProductHeader from "web/react/components/buybuybuy-area/product-header/product-header";
import { ExpandableText } from "web/react/components/expandable-text/expandable-text";
import { PricingCardPDP } from "web/react/components/pdp-pricing-card";
import ProductDescriptionComparePrice from "web/react/components/product-description/product-description-compare-prices/product-description-compare-prices";
import RelatedProductsSidebar from "web/react/components/related-products-sidebar/related-products-sidebar";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { Option } from "web/react/components/select/types";
import SizePicker from "web/react/components/size-picker/size-picker";
import { HStack } from "web/react/emo/h-stack";
import { Stack } from "web/react/emo/shared/components/stack";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page//in-stock-product-page.context";
import { FixedBuyOnStoreButton } from "web/react/pages/product/in-stock/in-stock-product-page/fixed-buy-on-store-button/fixed-buy-on-store-button";
import { ComparePriceLink } from "web/react/pages/product/in-stock/in-stock-product-page/product-price-comparison";
import { getBuyOptionStickerType } from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import { ProductImageGallery } from "web/react/pages/product/in-stock/product-image-gallery/product-image-gallery";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { canUseMembership } from "web/script/modules/utils";
import * as styles from "./product-area.css";

function handleSizeSelect(option: Option): void {
    const action = option.disabled ? "select_oos_alert" : "select_in_stock_size";
    analytics.event(
        "size_picker",
        action,
        option.selectedText,
        false,
        null,
        `affiliate.size_picker.${action}`,
        {},
        [],
        true
    );
}

function handleSizeExpanded(isExpanded: boolean): void {
    const action = isExpanded ? "close_size_picker" : "open_size_picker";
    analytics.event(
        "size_picker",
        action,
        undefined,
        false,
        null,
        `affiliate.size_picker.${action}`,
        null,
        undefined,
        true
    );
}

function ProductArea(): React.ReactElement {
    const { isMobileViewport } = useDomViewport();
    const {
        isRedesignPhaseTwo,
        designer,
        product,
        buyOptions,
        sortedBuyOptionsKeys,
        sizePicker: { sizes, isSizePickerExpanded, schemaLabels, userDefaultSchema },
        activeBuyOption: { buyOption: activeBuyOption, defaultSize, promotion },
        activeProduct: {
            selectedSizeOption,
            longDescription,
            cardSizeList,
            shippingInformation,
            productImageUrl,
            price,
        },
        isLoggedIn,
        setSelectedSizeOption,
        setIsSizePickerExpanded,
        relatedProductsSidebar,
    } = useInStockProductPageContext();

    const buyOnStoreBtnRef = useRef<HTMLImageElement | null>(null);
    const [isBuyOnStoreBtnVisible] = useIntersectionObserver(
        {
            threshold: 0,
            rootMargin: isLoggedIn ? "-55px" : "0px", // Logged in user have
        },
        buyOnStoreBtnRef as any
    );
    const [isBeforeMainCta, setIsBeforeMainCta] = useState(false);

    useEffect(() => {
        if (buyOnStoreBtnRef.current) {
            const position = buyOnStoreBtnRef.current.getBoundingClientRect().top;
            setIsBeforeMainCta(position > 0);
        }
    }, [isBuyOnStoreBtnVisible]);

    return (
        <Stack
            align={{ sm: "center", md: "center", lg: "start" }}
            direction={{ sm: "column-reverse", md: "column-reverse", lg: "row" }}
            spacing={isMobileViewport ? "md" : "lg"}
            justify="space-between"
        >
            <View className={styles.productInfoWrapper}>
                <VStack spacing="md">
                    <VStack spacing="xxs">
                        <HStack justify="space-between">
                            <ProductHeader
                                variant="emotional"
                                isOwnerActive={designer.active}
                                isOwnerFeedLinkable={designer.active}
                                ownerName={designer.name}
                                designerUrl={designer.url}
                                shortDescription={product.short_description}
                            />
                            {canUseMembership() && (
                                <SaveForLaterButton
                                    isLoggedIn={isLoggedIn}
                                    type="icon"
                                    variant="emotional"
                                    productId={product.product_id}
                                    pageSource="pdp"
                                    className={styles.saveForLaterBtn}
                                />
                            )}
                        </HStack>
                        <ExpandableText
                            analyticsCategory={"product-area"}
                            productId={product.product_id}
                        >
                            {longDescription}
                        </ExpandableText>
                    </VStack>
                    <VStack spacing="sm">
                        {sizes && (
                            <SizePicker
                                onSelect={(option: Option) => {
                                    handleSizeSelect(option);
                                    setSelectedSizeOption(option.value);
                                }}
                                isExpanded={isSizePickerExpanded}
                                schemas={schemaLabels}
                                // "Select size to see lowest price"
                                defaultSelectText={gettext("product.buy_area.select_size.label")}
                                userDefaultSchema={userDefaultSchema}
                                setExpanded={() => {
                                    handleSizeExpanded(isSizePickerExpanded);
                                    setIsSizePickerExpanded(!isSizePickerExpanded);
                                }}
                                sizes={sizes}
                                productType="affiliate"
                                showDisabledOptions={isRedesignPhaseTwo}
                            />
                        )}
                        <PricingCardPDP
                            price={price}
                            retailerName={activeBuyOption.retailer_name}
                            sizeList={cardSizeList}
                            shippingInformation={shippingInformation}
                            stickerType={getBuyOptionStickerType(
                                activeBuyOption,
                                sortedBuyOptionsKeys[0]
                            )}
                            leadUrl={defaultSize.lead_url}
                            isSizeSelected={selectedSizeOption !== null}
                            promotion={promotion}
                        />
                        <div ref={buyOnStoreBtnRef}>
                            <BuyOnStoreButton
                                productId={product.product_id}
                                leadUrl={defaultSize.lead_url}
                                leadLabel={gettext("product.buy_area.buy_now")}
                                analyticsEventLabel="is-product-shop-now"
                                productImageUrl={product.images[0].full_size_url}
                                contextType={"shop_now"}
                                hasPromotion={false}
                                retailerSlug={activeBuyOption.retailer_slug}
                            />
                        </div>
                        {isMobileViewport && (
                            <FixedBuyOnStoreButton
                                showButton={!isBuyOnStoreBtnVisible}
                                isBefore={isBeforeMainCta}
                            />
                        )}
                    </VStack>
                    {sortedBuyOptionsKeys && sortedBuyOptionsKeys.length > 1 && (
                        <>
                            {environment.getFeature("cw_pdp_redesign_phase_2") ? (
                                <ComparePriceLink productCount={sortedBuyOptionsKeys.length} />
                            ) : (
                                <span className={styles.compare}>
                                    <ProductDescriptionComparePrice
                                        buyOptions={buyOptions as any}
                                        sortedBuyOptions={sortedBuyOptionsKeys}
                                        inlineVariant={true}
                                        productImageUrl={productImageUrl}
                                        title={gettext(
                                            "product.buy_area.compare_count_prices.cta",
                                            {
                                                count: sortedBuyOptionsKeys.length,
                                            }
                                        )}
                                        showEmoRedesign={true}
                                    />
                                </span>
                            )}
                        </>
                    )}
                </VStack>
            </View>
            <div className={styles.galleryParent}>
                <View className={styles.imageGallery}>
                    <ProductImageGallery
                        images={product?.images?.map(({ alt_text, full_size_url, id }, i) => ({
                            id: id || `ProductImageGallery-${i.toString()}`, //  avoid using indices as keys
                            altText: alt_text,
                            url: full_size_url,
                        }))}
                        productId={product.product_id}
                    />
                </View>
            </div>
            {relatedProductsSidebar && (
                <div className={styles.sidebarParent}>
                    <View marginLeft={"sm"} marginTop={"sm"} className={styles.sidebarWrapper}>
                        <RelatedProductsSidebar
                            relatedProductsSidebar={relatedProductsSidebar}
                            variant="vertical"
                            className={clsx(styles.sidebar, "affiliateSidebar")}
                            isEmo
                        />
                    </View>
                </div>
            )}
        </Stack>
    );
}

export default ProductArea;
